import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaHistory,
  FaBell,
  FaLock,
  FaCog,
  FaSignOutAlt,
  FaArrowLeft,
  FaHeart,
  FaBars,
  FaTimes
} from "react-icons/fa";
import { FiHome, FiUser, FiShoppingBag, FiHeart, FiBell, FiSettings, FiLogOut, FiShield } from "react-icons/fi";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer";
import Profile from "./Profile/Profile";
import PurchaseHistory from "./PurchaseDetails/PurchaseHistory";
import Updates from "./Updates";
import PrivacyPolicy from "./PrivacyPolicy";
import Settings from "./Settings";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Wishlist from "./Wishlist";
import LogoutConfirmationModal from "./LogoutConfirmationModal";
import userImage from "../../assets/images/user.png";

const Dashboard = () => {
  const [selectedSection, setSelectedSection] = useState("profile");
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const { profile } = useSelector((state) => state.profile);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get("section");
    if (
      section &&
      [
        "profile",
        "purchase-history",
        "wishlist",
        "updates",
        "privacy-policy",
        "settings",
      ].includes(section)
    ) {
      setSelectedSection(section);
    }
  }, [location.search]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsSidebarVisible(true);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const menuItems = [
    {
      id: "profile",
      label: "Profile",
      icon: FiUser,
      description: "Personal information"
    },
    {
      id: "purchase-history",
      label: "My Tickets",
      icon: FiShoppingBag,
      description: "Purchase history"
    },
    {
      id: "wishlist",
      label: "Wishlist",
      icon: FiHeart,
      description: "Saved events"
    },
    {
      id: "updates",
      label: "Notifications",
      icon: FiBell,
      description: "Updates & alerts"
    },
    {
      id: "privacy-policy",
      label: "Privacy",
      icon: FiShield,
      description: "Privacy policy"
    },
    {
      id: "settings",
      label: "Settings",
      icon: FiSettings,
      description: "Account settings"
    }
  ];

  const renderContent = () => {
    switch (selectedSection) {
      case "profile":
        return <Profile />;
      case "purchase-history":
        return <PurchaseHistory />;
      case "wishlist":
        return <Wishlist />;
      case "updates":
        return <Updates />;
      case "privacy-policy":
        return <PrivacyPolicy />;
      case "settings":
        return <Settings />;
      default:
        return <Profile />;
    }
  };

  const handleLogoutClick = () => {
    setIsModalOpen(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    sessionStorage.removeItem("access");
    sessionStorage.removeItem("refresh");
    navigate("/login");
  };

  const handleMenuClick = (sectionId) => {
    setSelectedSection(sectionId);
    if (isMobile) {
      setIsSidebarVisible(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar />

      <div className="flex flex-1">
        {/* Desktop/Tablet Sidebar */}
        <div className={`
          ${isMobile ? 'hidden' : 'block'} 
          w-80 bg-white shadow-lg flex flex-col h-full
        `}>
          {/* Sidebar Header */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <img
                src={profile?.profile_picture || userImage}
                alt="Profile"
                className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
              />
              <div>
                <h3 className="font-semibold text-gray-900">
                  {profile?.full_name || 'User'}
                </h3>
                <p className="text-sm text-gray-600">
                  {profile?.email || 'user@example.com'}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <div className="flex-1 p-4 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = selectedSection === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => handleMenuClick(item.id)}
                  className={`
                    w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200
                    ${isActive 
                      ? 'bg-sunday_primary text-white shadow-lg' 
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                  <div className="text-left">
                    <p className={`font-medium ${isActive ? 'text-white' : 'text-gray-900'}`}>
                      {item.label}
                    </p>
                    <p className={`text-sm ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                      {item.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Logout Button */}
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={handleLogoutClick}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all duration-200"
            >
              <FiLogOut className="w-5 h-5" />
              <div className="text-left">
                <p className="font-medium">Logout</p>
                <p className="text-sm text-red-500">Sign out of account</p>
              </div>
            </button>
          </div>

          {/* Footer */}
          <div className="p-4 text-center border-t border-gray-100">
            <p className="text-xs text-gray-500">
              © 2025 EventFull Sunday
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Mobile Section Title */}
          {isMobile && (
            <div className="bg-white border-b border-gray-100 p-4">
              <h1 className="text-xl font-semibold text-gray-900 text-center capitalize">
                {selectedSection.replace('-', ' ')}
              </h1>
            </div>
          )}

          {/* Content Area */}
          <div className="flex-1 overflow-auto">
            <div className="p-4 md:p-8">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
};

export default Dashboard;
